import { CVSS20, CVSS30, CVSS31, CVSS40 } from "@pandatix/js-cvss";
import {
  CVSS2_VECTOR_REGEX,
  CVSS3_0_VECTOR_REGEX,
  CVSS3_1_VECTOR_REGEX,
  CVSS4_VECTOR_REGEX,
  VALID_CVSS_PREFIXES,
} from "constants/CVSSConstants";
import { isEmpty } from "lodash";

import { TagColors } from "ac-components/ac-tag-colored/ACTagColored";

import { CellRenderers } from "./tableUtils";

export const extractCVSSVersion = (vector: string) => {
  if (vector) {
    // Regular expression to match and capture the CVSS version number
    const versionRegex = /CVSS:(\d\.\d)/;
    const match = vector.match(versionRegex);
    if (match?.[1]) {
      return match[1]; // Returns the version number as a string, e.g., "2.0", "3.1"
    }
  }
  return null; // Returns null if no valid version is found
};

export const parseCVSSVectorStringToFields = (
  vector: string,
  isModifiedCVSS?: boolean,
) => {
  // Define a mapping from CVSS prefixes to suffixes
  const versionSuffixMap = {
    "CVSS:2.0": "20",
    "CVSS:3.0": "30",
    "CVSS:3.1": "31",
    "CVSS:4.0": "40",
  };

  if (!vector) return {};
  const matchedPrefix = VALID_CVSS_PREFIXES.find((prefix) =>
    vector.startsWith(prefix),
  ) as keyof typeof versionSuffixMap;
  if (matchedPrefix) {
    // Get the suffix based on the matched prefix
    const suffix = versionSuffixMap[matchedPrefix];

    const stringWithoutCVSSPrefix = vector.split(`${matchedPrefix}/`).pop();

    const parsedMetrics = parseCVSSMetricsStringToFields(
      stringWithoutCVSSPrefix,
    );

    // If no suffix needs to be added (modified CVSS) or no metrics were parsed

    if (isModifiedCVSS || Object.keys(parsedMetrics).length === 0 || !suffix) {
      return parsedMetrics;
    }

    const suffixedMetrics: Record<string, string> = {};

    Object.entries(parsedMetrics).forEach(([key, value]) => {
      suffixedMetrics[key + suffix] = value;
    });

    return suffixedMetrics;
  }
  return {};
};

export const parseCVSSMetricsStringToFields = (
  metricsString: string | undefined | null,
): Record<string, string> => {
  if (!metricsString || metricsString.trim().length === 0) {
    return {};
  }

  const fieldsArray = metricsString.split("/");
  const fieldValues: Record<string, string> = {};

  fieldsArray.forEach((pair) => {
    const parts = pair.split(":");
    if (parts.length === 2) {
      const field = parts[0];
      const value = parts[1];
      fieldValues[field] = value;
    }
  });
  return fieldValues;
};

export const getCvssScoreFromVector = (str: string) => {
  if (isEmpty(str)) {
    return;
  }

  if (!validateCVSSVectorFormat(str)) {
    return;
  }

  const versionPrefix = str.match(/^CVSS:(\d+\.\d+)/);
  let vectorForScoreCalculation = str;

  // Check if it's a CVSS 2.0 vector and remove the prefix
  if (str.startsWith("CVSS:2.0/")) {
    vectorForScoreCalculation = str.slice(9); // Remove "CVSS:2.0/"
  }

  if (!versionPrefix) {
    throw new Error("Invalid CVSS vector string: Version prefix not found.");
  }

  const version = versionPrefix[1]; // Capture the numeric version

  switch (version) {
    case "2.0": {
      const cvss2 = new CVSS20(vectorForScoreCalculation);
      return cvss2.BaseScore();
    }

    case "3.0": {
      const cvss3_0 = new CVSS30(vectorForScoreCalculation);
      return cvss3_0.BaseScore();
    }

    case "3.1": {
      const cvss3_1 = new CVSS31(vectorForScoreCalculation);
      return cvss3_1.BaseScore();
    }

    case "4.0": {
      const cvss4 = new CVSS40(vectorForScoreCalculation);
      return cvss4.Score();
    }

    default:
      throw new Error("Invalid CVSS version.");
  }
};

export const cvssVectorValidator = () => {
  return {
    validator: (_rule: unknown, value: string) => {
      if (isEmpty(value)) {
        return Promise.resolve(); // Empty value is considered valid
      }
      if (validateCVSSVectorFormat(value) === false) {
        return Promise.reject("Not a valid CVSS vector.");
      }
      return Promise.resolve(); // Valid CVSS vector
    },
  };
};

export function validateCVSSVectorFormat(value: string | undefined) {
  if (!value || value.trim().length === 0) {
    return true;
  }
  const isValidPrefix = VALID_CVSS_PREFIXES.some((prefix) =>
    value.startsWith(prefix),
  );
  if (!isValidPrefix) {
    return false;
  }
  let moreThanOnePrefix = false;
  const prefixMatch = value.match(/CVSS:(2\.0|3\.0|3\.1|4\.0)/g);
  if (!prefixMatch || prefixMatch.length > 1) {
    moreThanOnePrefix = true;
  }
  const isValid =
    CVSS2_VECTOR_REGEX.test(value) ||
    CVSS3_0_VECTOR_REGEX.test(value) ||
    CVSS3_1_VECTOR_REGEX.test(value) ||
    CVSS4_VECTOR_REGEX.test(value);
  if (!isValid || moreThanOnePrefix === true) {
    return false;
  }
  return true;
}

export const getStringFromModifiedCVSSObject = (
  value: { [key: string]: string },
  version?: string,
) => {
  const metrics = Object.keys(value)
    // Filter out the 'CVSS' key to prevent duplication
    .filter((key) => key !== "CVSS")
    .map((key) => `${key}:${value[key]}`)
    .join("/");

  if (version) {
    // If there are no metrics, just return the version string.
    if (metrics.length === 0) {
      return `CVSS:${version}`;
    }

    return `CVSS:${version}/${metrics}`;
  } else {
    return metrics;
  }
};

export const getColorAndSeverityByScore = (
  score?: number,
): {
  severity: string;
  color: TagColors;
} => {
  if (score) {
    if (score >= 9 && score <= 10) {
      return {
        severity: "Critical",
        color: "Error",
      };
    } else if (score >= 7 && score <= 8.9) {
      return {
        severity: "High",
        color: "Orange",
      };
    } else if (score >= 4 && score <= 6.9) {
      return {
        severity: "Medium",
        color: "Yellow",
      };
    } else if (score >= 0.1 && score <= 3.9) {
      return {
        severity: "Low",
        color: "Green",
      };
    }
  }
  return {
    severity: CellRenderers.VALUE_OR_NA(),
    color: "Neutral",
  };
};
